import React from 'react';
import styled from 'styled-components';

import Backdrop from '@webtv/components/backdrop';
import Button from '@webtv/components/button';
import Container from '@webtv/components/container';
import Link from '@webtv/components/link';
import Typography from '@webtv/components/typography';
import testIds from '@webtv/e2e/shared/variables';
import useBrand from '@webtv/hooks/use-brand';
import useCookieConsent from '@webtv/hooks/use-cookie-consent';

import CookieBannerDialog from './cookie-banner-dialog';

const CookieBannerRoot = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  color: ${(props) => props.theme.palette.common.black};
  background-color: ${(props) => props.theme.palette.background.level2};
`;

const CookieBannerHeader = styled(Typography)`
  margin-bottom: ${(props) => props.theme.spacing(5)};
  font-weight: ${(props) => props.theme.typography.weight.regular};
`;

const CookieBannerText = styled(Typography)`
  margin-bottom: ${(props) => props.theme.spacing(5)};
`;

const CookieBannerLinks = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(5)};
`;

const CookieBannerButtons = styled.div`
  display: flex;

  button:first-child {
    margin-right: ${(props) => props.theme.spacing(5)};
  }
`;

/**
 * The CookieBanner component is used to display some cookie information for the user on the first visit to the app.
 * The user can then choose to either reject or accept the cookies before browsing on the site.
 */
export const CookieBanner: React.FC = () => {
  const [openCookieBannerDialog, setOpenCookieBannerDialog] = React.useState(false);

  const { isStofa } = useBrand();

  const { handleAcceptCookies, handleRejectCookies } = useCookieConsent();

  const handleOpenCookieBannerDialog = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      setOpenCookieBannerDialog(true);
    },
    []
  );

  const handleCloseCookieBannerDialog = React.useCallback(() => {
    setOpenCookieBannerDialog(false);
  }, []);

  const brandName = isStofa ? 'Stofa A/S' : 'Norlys';

  return (
    <Backdrop open={true}>
      <CookieBannerRoot>
        <Container>
          <CookieBannerHeader variant="h2">Denne hjemmeside bruger cookies</CookieBannerHeader>

          <CookieBannerText>
            {`${brandName} bruger cookies til at tilpasse vores indhold og annoncer, til at vise dig
              funktioner til sociale medier og til at analysere vores trafik. Vi deler også
              oplysninger om din brug af vores website med vores partnere inden for sociale medier,
              annonceringspartnere og analysepartnere. Vores partnere kan kombinere disse data med
              andre oplysninger, du har givet dem, eller som de har indsamlet fra din brug af deres
              tjenester. Du samtykker til vores cookies, hvis du fortsætter med at anvende vores
              hjemmeside.`}
          </CookieBannerText>

          <CookieBannerLinks>
            <Link
              color="inherit"
              href={isStofa ? 'https://stofa.dk/om/cookies' : 'https://norlys.dk/cookiepolitik/'}
              rel="noreferrer"
              target="_blank"
              underline="always"
              data-testid={testIds.cookieBanner.cookiePolicy}
            >
              Læs mere om vores cookiepolitik
            </Link>

            <Typography component="span">&nbsp;|&nbsp;</Typography>

            <Link
              color="inherit"
              href="#"
              underline="always"
              onClick={handleOpenCookieBannerDialog}
              data-testid={testIds.cookieBanner.showCookies}
            >
              Vis cookies
            </Link>
          </CookieBannerLinks>

          <CookieBannerButtons>
            <Button
              color="primary"
              onClick={() => handleAcceptCookies()}
              data-testid={testIds.cookieBanner.accept}
            >
              Accepter
            </Button>

            <Button onClick={() => handleRejectCookies()} data-testid={testIds.cookieBanner.reject}>
              Afvis
            </Button>
          </CookieBannerButtons>
        </Container>

        <CookieBannerDialog open={openCookieBannerDialog} onClose={handleCloseCookieBannerDialog} />
      </CookieBannerRoot>
    </Backdrop>
  );
};

export default React.memo(CookieBanner);
